import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { ConfirmDialog } from "@/client/components/confirm-dialog";
import { PageHeader } from "@/client/components/page-header";
import { Badge } from "@/client/components/ui/badge";
import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/client/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/client/components/ui/form";
import { Input } from "@/client/components/ui/input";
import { Skeleton } from "@/client/components/ui/skeleton";
import { useAuthStore } from "@/client/features/auth/auth-store";
import { GuideLink } from "@/client/features/docs/placements";
import {
  type MemberSshKey,
  type PendingInvite,
  teamApi,
  type TeamMember,
} from "@/client/features/team/team-api";
import { ApiError } from "@/client/lib/api";
import type { TenantRole } from "@/shared/types";

const textareaClass =
  "border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 min-h-24 w-full rounded-md border px-3 py-2 font-mono text-sm shadow-xs focus-visible:ring-[3px] focus-visible:outline-none";

function roleLabel(role: TenantRole): string {
  return role === "tenant_admin" ? "Admin" : "Member";
}

function MemberRoleBadge({ member }: { member: TeamMember }) {
  if (member.tenantRole === "tenant_admin") {
    return <Badge variant="secondary">Admin</Badge>;
  }
  return <Badge variant="outline">Member</Badge>;
}

function StatusBadge({ status }: { status: string }) {
  return (
    <Badge variant={status === "active" ? "outline" : "destructive"}>
      {status === "active" ? "Active" : status}
    </Badge>
  );
}

function RemoveMemberDialog({ member, onRemoved }: { member: TeamMember; onRemoved: () => void }) {
  const deactivated = useAuthStore((s) => s.deactivated);
  return (
    <ConfirmDialog
      trigger={
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          disabled={deactivated}
        >
          Remove
        </Button>
      }
      title="Remove member?"
      description={
        <>
          This removes {member.name ?? member.email} from your organization and signs them out
          immediately. They'll need a new invitation to rejoin.
        </>
      }
      confirmLabel="Remove"
      pendingLabel="Removing…"
      errorMessage="Couldn't remove this member."
      onConfirm={async () => {
        await teamApi.removeMember(member.id);
        toast.success(`Removed ${member.name ?? member.email}.`);
        onRemoved();
      }}
    />
  );
}

const addKeySchema = z.object({
  publicKey: z.string().min(1, "Paste an SSH public key."),
  label: z.string().max(100, "Keep the label under 100 characters.").optional(),
});

type AddKeyValues = z.infer<typeof addKeySchema>;

function AddMemberKeyForm({ member, onAdded }: { member: TeamMember; onAdded: () => void }) {
  const deactivated = useAuthStore((s) => s.deactivated);
  const form = useForm<AddKeyValues>({
    resolver: zodResolver(addKeySchema),
    defaultValues: { publicKey: "", label: "" },
  });

  const onSubmit = async (values: AddKeyValues) => {
    try {
      await teamApi.addMemberKey(member.id, {
        publicKey: values.publicKey,
        label: values.label?.trim() || undefined,
      });
      toast.success("SSH key added.");
      form.reset({ publicKey: "", label: "" });
      onAdded();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Unable to add the key. Please try again.",
      );
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-3">
        <FormField
          control={form.control}
          name="publicKey"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Public key</FormLabel>
              <FormControl>
                <textarea
                  className={textareaClass}
                  placeholder="ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAA... user@host"
                  spellCheck={false}
                  {...field}
                />
              </FormControl>
              <FormDescription>Only ed25519 keys are supported.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="label"
          render={({ field }) => (
            <FormItem className="sm:max-w-xs">
              <FormLabel>Label (optional)</FormLabel>
              <FormControl>
                <Input placeholder="Work laptop" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          className="w-fit"
          size="sm"
          disabled={form.formState.isSubmitting || deactivated}
        >
          {form.formState.isSubmitting ? "Adding…" : "Add key"}
        </Button>
      </form>
    </Form>
  );
}

function MemberKeysList({
  member,
  keys,
  error,
  onChanged,
}: {
  member: TeamMember;
  keys: MemberSshKey[] | null;
  error: boolean;
  onChanged: () => void;
}) {
  const [removingId, setRemovingId] = useState<string | null>(null);
  const deactivated = useAuthStore((s) => s.deactivated);

  const remove = async (keyId: string) => {
    setRemovingId(keyId);
    try {
      await teamApi.removeMemberKey(member.id, keyId);
      toast.success("SSH key removed.");
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Unable to remove the key.");
    } finally {
      setRemovingId(null);
    }
  };

  if (keys === null) {
    // No data yet: distinguish an initial load failure from still-loading. Once
    // a list has loaded, a later refresh failure keeps the (stale) list visible.
    return error ? (
      <p className="text-muted-foreground text-sm">Couldn't load this member's keys.</p>
    ) : (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }
  if (keys.length === 0) {
    return <p className="text-muted-foreground text-sm">No SSH keys yet.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-muted-foreground border-b text-left">
            <th className="py-2 pr-4 font-medium">Label</th>
            <th className="py-2 pr-4 font-medium">Fingerprint</th>
            <th className="py-2 font-medium" />
          </tr>
        </thead>
        <tbody>
          {keys.map((k) => (
            <tr key={k.id} className="border-b last:border-0">
              <td className="py-2 pr-4">{k.label ?? k.comment ?? "—"}</td>
              <td className="py-2 pr-4 font-mono text-xs break-all">{k.fingerprint}</td>
              <td className="py-2 text-right">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => remove(k.id)}
                  disabled={removingId === k.id || deactivated}
                >
                  {removingId === k.id ? "Removing…" : "Remove"}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ManageKeysDialog({ member }: { member: TeamMember }) {
  const [open, setOpen] = useState(false);
  const [keys, setKeys] = useState<MemberSshKey[] | null>(null);
  const [error, setError] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await teamApi.listMemberKeys(member.id);
      setKeys(res.keys);
      setError(false);
    } catch (err) {
      setError(true);
      toast.error(err instanceof ApiError ? err.message : "Failed to load this member's keys.");
    }
  }, [member.id]);

  useEffect(() => {
    if (open) {
      setKeys(null);
      setError(false);
      void refresh();
    }
  }, [open, refresh]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          Manage keys
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>SSH keys</DialogTitle>
          <DialogDescription>Keys for {member.name ?? member.email}.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-6">
          <AddMemberKeyForm member={member} onAdded={refresh} />
          <MemberKeysList member={member} keys={keys} error={error} onChanged={refresh} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MembersTable({
  members,
  isAdmin,
  currentUserId,
  onRemoved,
}: {
  members: TeamMember[];
  isAdmin: boolean;
  currentUserId: string | undefined;
  onRemoved: () => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-muted-foreground border-b text-left">
            <th className="py-2 pr-4 font-medium">Name</th>
            <th className="py-2 pr-4 font-medium">Email</th>
            <th className="py-2 pr-4 font-medium">Role</th>
            <th className="py-2 font-medium">Status</th>
            {isAdmin ? <th className="py-2 text-right font-medium">Actions</th> : null}
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.id} className="border-b last:border-0">
              <td className="py-2 pr-4">{m.name ?? "—"}</td>
              <td className="text-muted-foreground py-2 pr-4">{m.email}</td>
              <td className="py-2 pr-4">
                <MemberRoleBadge member={m} />
              </td>
              <td className="py-2">
                <StatusBadge status={m.status} />
              </td>
              {isAdmin ? (
                <td className="py-2">
                  <div className="flex items-center justify-end gap-1">
                    {m.status === "active" ? <ManageKeysDialog member={m} /> : null}
                    {/* Admins can't remove themselves (use another admin); the
                        server enforces this and the last-admin guard too. */}
                    {m.id !== currentUserId && m.status === "active" ? (
                      <RemoveMemberDialog member={m} onRemoved={onRemoved} />
                    ) : null}
                  </div>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const inviteSchema = z.object({
  email: z.string().email("Enter a valid email"),
  role: z.enum(["member", "tenant_admin"]),
});

type InviteValues = z.infer<typeof inviteSchema>;

function InviteForm({ onCreated }: { onCreated: () => void }) {
  const deactivated = useAuthStore((s) => s.deactivated);
  const form = useForm<InviteValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: "", role: "member" },
  });

  const onSubmit = async (values: InviteValues) => {
    try {
      await teamApi.createInvite(values);
      toast.success(`Invitation sent to ${values.email}.`);
      form.reset({ email: "", role: "member" });
      onCreated();
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Unable to send the invitation. Please try again.";
      toast.error(message);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invite a teammate</CardTitle>
        <CardDescription>They'll get an email with a link to set up their account.</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4 sm:flex-row sm:items-end"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="teammate@company.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem className="sm:w-40">
                  <FormLabel>Role</FormLabel>
                  <FormControl>
                    <select
                      className="border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 h-9 w-full rounded-md border px-3 py-1 text-sm shadow-xs focus-visible:ring-[3px] focus-visible:outline-none"
                      {...field}
                    >
                      <option value="member">Member</option>
                      <option value="tenant_admin">Admin</option>
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={form.formState.isSubmitting || deactivated}>
              {form.formState.isSubmitting ? "Sending…" : "Send invite"}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}

function PendingInvites({ invites, error }: { invites: PendingInvite[] | null; error: boolean }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending invites</CardTitle>
        <CardDescription>People who've been invited but haven't joined yet.</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Prefer showing whatever list we have: a failed refresh (e.g. after a
            successful invite) shouldn't wipe a roster we already loaded. The
            toast already surfaces the refresh failure. */}
        {invites && invites.length > 0 ? (
          <ul className="divide-y">
            {invites.map((inv) => (
              <li key={inv.id} className="flex items-center justify-between gap-4 py-2 text-sm">
                <div className="min-w-0">
                  <p className="truncate">{inv.email}</p>
                  <p className="text-muted-foreground text-xs">
                    Expires {new Date(inv.expiresAt).toLocaleDateString()}
                  </p>
                </div>
                <Badge variant={inv.role === "tenant_admin" ? "secondary" : "outline"}>
                  {roleLabel(inv.role)}
                </Badge>
              </li>
            ))}
          </ul>
        ) : error ? (
          <p className="text-muted-foreground text-sm">
            Couldn't load pending invites. Refresh to try again.
          </p>
        ) : invites === null ? (
          <Skeleton className="h-8 w-full" />
        ) : (
          <p className="text-muted-foreground text-sm">No pending invites.</p>
        )}
      </CardContent>
    </Card>
  );
}

export function TeamPage() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.kind === "customer" && user.tenantRole === "tenant_admin";

  const [members, setMembers] = useState<TeamMember[] | null>(null);
  const [membersError, setMembersError] = useState(false);
  const [invites, setInvites] = useState<PendingInvite[] | null>(null);
  const [invitesError, setInvitesError] = useState(false);
  const [loading, setLoading] = useState(true);

  const refreshInvites = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await teamApi.listInvites();
      setInvites(res.invites);
      setInvitesError(false);
    } catch (err) {
      setInvitesError(true);
      toast.error(err instanceof ApiError ? err.message : "Failed to load pending invites.");
    }
  }, [isAdmin]);

  const refreshMembers = useCallback(async () => {
    try {
      const res = await teamApi.listMembers();
      setMembers(res.users);
      setMembersError(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to refresh your team.");
    }
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const res = await teamApi.listMembers();
        if (active) {
          setMembers(res.users);
          setMembersError(false);
        }
      } catch (err) {
        if (active) {
          setMembersError(true);
          toast.error(err instanceof ApiError ? err.message : "Failed to load your team.");
        }
      } finally {
        if (active) setLoading(false);
      }
      if (!isAdmin) return;
      try {
        const inv = await teamApi.listInvites();
        if (active) {
          setInvites(inv.invites);
          setInvitesError(false);
        }
      } catch (err) {
        if (active) {
          setInvitesError(true);
          toast.error(err instanceof ApiError ? err.message : "Failed to load pending invites.");
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [isAdmin]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Your team"
        description="People in your organization with access to the portal."
      >
        <GuideLink slot="team.help" />
      </PageHeader>

      {isAdmin ? <InviteForm onCreated={refreshInvites} /> : null}

      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
          <CardDescription>Everyone with an active or pending account.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : membersError ? (
            <p className="text-muted-foreground text-sm">
              Couldn't load your team. Refresh to try again.
            </p>
          ) : members && members.length > 0 ? (
            <MembersTable
              members={members}
              isAdmin={isAdmin}
              currentUserId={user?.id}
              onRemoved={refreshMembers}
            />
          ) : (
            <p className="text-muted-foreground text-sm">No members yet.</p>
          )}
        </CardContent>
      </Card>

      {isAdmin ? <PendingInvites invites={invites} error={invitesError} /> : null}
    </div>
  );
}
